import axios, { AxiosError, AxiosInstance } from "axios";
import { StatusCode } from "../enums/index";
import { ApiConsumer } from "./ApiConsumer";
import store from "../store/store";
import { setIsConnectionLost } from "../store/config-slice";

const REAUTH_RETRIES = 3;
let axiosInstance: AxiosInstance | undefined;
let accessToken = "";
let refreshToken = "";
let reauthTries = 0;

const API_REQUEST_TIMEOUT = 10000;

const isNetworkError = (error: AxiosError): boolean =>
  error.message.includes("Network Error") ||
  error.message.includes(`timeout of ${API_REQUEST_TIMEOUT}ms exceeded`);

const setConnectionLostStatus = (isConnectionLost: boolean): void => {
  store.dispatch(setIsConnectionLost(isConnectionLost));
};

// eslint-disable-next-line @typescript-eslint/no-empty-function
let setReAuthError = (): void => {};

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
export const getAxiosInstance = (): AxiosInstance => axiosInstance!;

const initInterceptors = (): void => {
  getAxiosInstance().interceptors.request.use(
    (config) => {
      if (config && config.headers) {
        // eslint-disable-next-line no-param-reassign
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
      return config;
    },
    (error) => {
      // eslint-disable-next-line no-console
      console.log(`Problem: ${error}`);
    }
  );

  getAxiosInstance().interceptors.response.use(
    (response) => response,
    async (error) => {
      if (isNetworkError(error)) {
        setConnectionLostStatus(true);
      } else if (
        error.response &&
        error.response.status === StatusCode.UNAUTHORIZED &&
        axiosInstance
      ) {
        /* eslint-disable no-plusplus */
        if (++reauthTries >= REAUTH_RETRIES) {
          setReAuthError();
          return Promise.reject(error);
        }

        const failedRequest = error.config;

        accessToken = refreshToken;

        const response = await ApiConsumer.refreshAccessToken();
        accessToken = response.accessToken;
        refreshToken = response.refreshToken;

        failedRequest.headers.Authorization = `Bearer ${accessToken}`;
        reauthTries = 0;

        return axios.request(failedRequest);
      }

      // eslint-disable-next-line no-console
      console.log(`Problem on response: ${error}`);
      return Promise.reject(error.response.status);
    }
  );
};

export const initAxios = (
  baseURL: string,
  initialAccessToken: string,
  initialRefreshToken: string,
  reauthErrorCallback: () => void
): void => {
  setReAuthError = reauthErrorCallback;
  accessToken = initialAccessToken;
  refreshToken = initialRefreshToken;
  axiosInstance = axios.create({
    baseURL: baseURL.endsWith("/") ? baseURL : baseURL.concat("/"),
    withCredentials: true,
    headers: {
      "Content-Type": "application/json",
    },
    timeout: API_REQUEST_TIMEOUT,
  });
  initInterceptors();
};
